import { ReactNode } from 'react'

import { Toast } from '@base-ui/react/toast'

export const toastManager = Toast.createToastManager()

const DEFAULT_TIMEOUT = 3000

export interface IToastOptions {
  timeout?: number
  key?: string
  description?: ReactNode
  action?: { text: string; onClick: () => void }
  cancel?: { text: string; onClick?: () => void }
}

type ToastType = 'success' | 'error' | 'warning' | 'info'

// base-ui mints its own ids, so a caller-supplied `key` is resolved against this
// map to update the live toast in place instead of stacking a duplicate.
const idsByKey = new Map<string, string>()

function releaseKey(key: string, id: string): void {
  if (idsByKey.get(key) === id) {
    idsByKey.delete(key)
  }
}

/**
 * `Toast.Provider` subscribes to the manager from a passive effect, and every
 * class `componentDidMount` in the tree runs before any passive effect — so a
 * toast raised while mounting (e.g. a denied deep link in Logistics) would
 * otherwise emit to zero listeners and vanish. Reordering the `<Toaster>` cannot
 * fix that: sibling order only reorders effects within a phase, not across them.
 * So calls are held until the viewport reports itself subscribed, then replayed
 * in order.
 */
let isViewportReady = false
const pending: Array<() => void> = []

function whenReady(run: () => void): void {
  if (isViewportReady) {
    run()
    return
  }
  pending.push(run)
  warnIfNeverFlushed()
}

/**
 * Buffering is only ever meant to bridge the app's mount, so anything still
 * queued a couple of seconds later means no `<Toaster>` is rendering against
 * this manager — every toast in the app would be silently swallowed. Say so
 * rather than losing them quietly.
 */
let hasScheduledWarning = false

function warnIfNeverFlushed(): void {
  if (process.env.NODE_ENV === 'production' || hasScheduledWarning) {
    return
  }
  hasScheduledWarning = true
  setTimeout(() => {
    if (!isViewportReady) {
      console.error(
        `[toast] ${pending.length} toast(s) queued with no viewport listening. ` +
          'Is <Toaster> mounted (AppProvider), and does it use this manager?',
      )
    }
  }, 2000)
}

/** Called by `<Toaster>` once its provider has subscribed. */
export function flushQueuedToasts(): void {
  isViewportReady = true
  pending.splice(0).forEach(run => run())
}

function show(
  type: ToastType,
  message: ReactNode,
  options: IToastOptions = {},
): void {
  const {
    timeout = DEFAULT_TIMEOUT,
    key,
    description,
    action,
    cancel,
  } = options

  const payload = {
    type,
    title: message,
    description,
    timeout,
    priority: type === 'error' ? ('high' as const) : ('low' as const),
    actionProps: action && { children: action.text, onClick: action.onClick },
    // base-ui has no cancel slot; the primitive reads it back off `data`.
    data: cancel ? { cancel } : undefined,
  }

  whenReady(() => {
    if (!key) {
      toastManager.add(payload)
      return
    }

    const liveId = idsByKey.get(key)
    if (liveId) {
      toastManager.update(liveId, payload)
      return
    }

    const id = toastManager.add({
      ...payload,
      onClose: () => releaseKey(key, id),
      onRemove: () => releaseKey(key, id),
    })
    idsByKey.set(key, id)
  })
}

/**
 * Drops every key→id mapping. `Toast.Provider` subscribes to the manager in an
 * effect, so anything toasted before the app mounts emits to zero listeners and
 * yields an id no viewport ever rendered — leaving that key permanently on the
 * `update` path, which no-ops. The mount clears the slate; child effects run
 * before the parent's componentDidMount, so by then the viewport is listening.
 */
export function resetKeyedToasts(): void {
  idsByKey.clear()
}

export const toast = {
  success: (message: ReactNode, options?: IToastOptions) =>
    show('success', message, options),
  error: (message: ReactNode, options?: IToastOptions) =>
    show('error', message, options),
  warning: (message: ReactNode, options?: IToastOptions) =>
    show('warning', message, options),
  info: (message: ReactNode, options?: IToastOptions) =>
    show('info', message, options),
  dismiss: (key: string) => {
    whenReady(() => {
      const id = idsByKey.get(key)
      if (id) {
        idsByKey.delete(key)
        toastManager.close(id)
      }
    })
  },
}
